<template>
  <div v-if="!isRippleAdmin && developerMode == false" class="sales-dashboard-page">
    <section class="dashboard-section">
      <div class="dashboard-section-header">
        <h2>Current Month</h2>
      </div>

      <div class="metric-grid metric-grid-current">
        <a-spin :spinning="spinner.sales">
          <article class="metric-tile">
            <p class="metric-kicker">Sales Today</p>
            <h3 class="metric-figure">{{ dashboard.currentMonth.salesToday }}</h3>
            <span class="metric-note">Live store sales</span>
          </article>
        </a-spin>

        <a-spin :spinning="spinner.yesterday_sales">
          <article class="metric-tile">
            <p class="metric-kicker">Sales Yesterday</p>
            <h3 class="metric-figure">{{ dashboard.currentMonth.salesYesterday }}</h3>
            <span class="metric-note">Previous trading day</span>
          </article>
        </a-spin>

        <a-spin :spinning="spinner.month_to_date_sales">
          <article class="metric-tile">
            <p class="metric-kicker">Sales Month To Date</p>
            <h3 class="metric-figure">{{ dashboard.currentMonth.salesMonthToDate }}</h3>
            <span class="metric-note">Current month total</span>
          </article>
        </a-spin>

        <a-spin :spinning="spinner.month_ave_sales">
          <article class="metric-tile">
            <p class="metric-kicker">Average Daily Sales</p>
            <h3 class="metric-figure">{{ dashboard.currentMonth.averageDailySales }}</h3>
            <span class="metric-note">Current month average</span>
          </article>
        </a-spin>
      </div>
    </section>

    <section class="dashboard-section">
      <div class="dashboard-section-header">
        <h2>Previous Month</h2>
      </div>

      <div class="metric-grid metric-grid-history">
        <a-spin :spinning="spinner.previous_month">
          <article class="metric-tile">
            <p class="metric-kicker">Total Sales</p>
            <h3 class="metric-figure">{{ dashboard.previousMonth.totalSales }}</h3>
          </article>
        </a-spin>

        <a-spin :spinning="spinner.previous_month">
          <article class="metric-tile">
            <p class="metric-kicker">Total Cost</p>
            <h3 class="metric-figure">{{ dashboard.previousMonth.totalCost }}</h3>
          </article>
        </a-spin>

        <a-spin :spinning="spinner.previous_month">
          <article class="metric-tile">
            <p class="metric-kicker">Gross Margin</p>
            <h3 class="metric-figure">{{ dashboard.previousMonth.grossMargin }}</h3>
          </article>
        </a-spin>

        <a-spin :spinning="spinner.previous_month">
          <article class="metric-tile">
            <p class="metric-kicker">Gross Margin %</p>
            <h3
              class="metric-figure"
              :class="percentClass(dashboard.previousMonth.grossMarginPercentRaw)"
            >
              {{ dashboard.previousMonth.grossMarginPercent }}
            </h3>
          </article>
        </a-spin>

        <a-spin :spinning="spinner.previous_month">
          <article class="metric-tile">
            <p class="metric-kicker">Growth Vs Year Ago</p>
            <h3
              class="metric-figure"
              :class="percentClass(dashboard.previousMonth.growthVsYearAgoRaw)"
            >
              {{ dashboard.previousMonth.growthVsYearAgo }}
            </h3>
          </article>
        </a-spin>

        <a-spin :spinning="spinner.previous_month">
          <article class="metric-tile">
            <p class="metric-kicker">Growth Vs Month Ago</p>
            <h3
              class="metric-figure"
              :class="percentClass(dashboard.previousMonth.growthVsMonthAgoRaw)"
            >
              {{ dashboard.previousMonth.growthVsMonthAgo }}
            </h3>
          </article>
        </a-spin>

        <a-spin :spinning="spinner.previous_month">
          <article class="metric-tile">
            <p class="metric-kicker">Average Daily Sales</p>
            <h3 class="metric-figure">{{ dashboard.previousMonth.averageDailySales }}</h3>
          </article>
        </a-spin>

        <a-spin :spinning="spinner.previous_month">
          <article class="metric-tile">
            <p class="metric-kicker">Average Basket Size</p>
            <h3 class="metric-figure">{{ dashboard.previousMonth.averageBasketSize }}</h3>
          </article>
        </a-spin>
      </div>
    </section>

    <section class="dashboard-section dashboard-section-chart">
      <div class="dashboard-section-header">
        <h2>Past 30 Days Sales</h2>
      </div>

      <a-spin :spinning="spinner.last_30_days_chart">
        <div class="chart-panel">
          <canvas id="sales30DaysChart"></canvas>
        </div>
      </a-spin>
    </section>

    <section class="dashboard-section">
      <div class="dashboard-section-header">
        <h2>Top 10 Products (Past 30 Days)</h2>
      </div>

      <a-spin :spinning="spinner.top_products">
        <div class="table-responsive">
          <table class="dashboard-table">
            <thead>
              <tr>
                <th>Rank</th>
                <th>Product Name</th>
                <th>Daily Sales Avg</th>
                <th>Daily Qty Sold</th>
                <th>Inventory</th>
                <th>Days On Hand</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="product in dashboard.topProducts"
                :key="product.rank + '-' + product.productName"
              >
                <td>
                  <span class="rank-badge">{{ product.rank }}</span>
                </td>
                <td class="dashboard-table-name">{{ product.productName }}</td>
                <td>{{ product.dailySalesAvg }}</td>
                <td>{{ product.dailyQtySold }}</td>
                <td :class="numberClass(product.inventoryRaw)">{{ product.inventory }}</td>
                <td>{{ product.daysOnHand }}</td>
              </tr>
              <tr v-if="dashboard.topProducts.length === 0">
                <td colspan="6" class="dashboard-table-empty">
                  No product data available.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </a-spin>
    </section>

    <section class="dashboard-section">
      <div class="dashboard-section-header">
        <h2>Top 10 Stores (Past 30 Days)</h2>
      </div>

      <a-spin :spinning="spinner.top_stores">
        <div class="table-responsive">
          <table class="dashboard-table">
            <thead>
              <tr>
                <th>Rank</th>
                <th>Store Name</th>
                <th>Total Sales</th>
                <th>Total Cost</th>
                <th>Total Gross Margin</th>
                <th>Gross Margin %</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="store in dashboard.topStores"
                :key="store.rank + '-' + store.storeName"
              >
                <td>
                  <span class="rank-badge">{{ store.rank }}</span>
                </td>
                <td class="dashboard-table-name">{{ store.storeName }}</td>
                <td>{{ store.totalSales }}</td>
                <td>{{ store.totalCost }}</td>
                <td :class="numberClass(store.totalGrossMarginRaw)">
                  {{ store.totalGrossMargin }}
                </td>
                <td :class="numberClass(store.grossMarginPercentRaw)">
                  {{ store.grossMarginPercent }}
                </td>
              </tr>
              <tr v-if="dashboard.topStores.length === 0">
                <td colspan="6" class="dashboard-table-empty">
                  No store data available.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </a-spin>
    </section>
  </div>
</template>

<script>
import secureStorage from "../controllers/secureStorage.js";
import HTTPRequest from "../controllers/httpRequest.js";
import moment from "moment";
import { mapState, mapActions } from "vuex";
var Chart = require("chart.js");

var component_x;
var sales30DaysChart;

export default {
  computed: {
    ...mapState(["loggedIn", "endPoint", "serverType"]),
  },
  data() {
    return {
      developerMode: secureStorage.getItem("developerMode"),
      isRippleAdmin: secureStorage.getItem("isRippleAdmin"),
      site_type: "ho",
      dashboard: {
        currentMonth: {
          salesToday: "0.00",
          salesYesterday: "0.00",
          salesMonthToDate: "0.00",
          averageDailySales: "0.00",
        },
        previousMonth: {
          totalSales: "0.00",
          totalCost: "0.00",
          grossMargin: "0.00",
          grossMarginPercent: "0.0%",
          grossMarginPercentRaw: 0,
          growthVsYearAgo: "0.0%",
          growthVsYearAgoRaw: 0,
          growthVsMonthAgo: "0.0%",
          growthVsMonthAgoRaw: 0,
          averageDailySales: "0.00",
          averageBasketSize: "0.00",
        },
        topProducts: [],
        topStores: [],
      },
      spinner: {
        sales: false,
        month_to_date_sales: false,
        yesterday_sales: false,
        month_ave_sales: false,
        previous_month: false,
        last_30_days_chart: false,
        top_products: false,
        top_stores: false,
      },
    };
  },
  mounted: async function () {
    component_x = this;

    if (this.loggedIn == false && this.developerMode == false) {
      this.$router.push("/");
      return;
    }

    this.setCurrentProfile("pos_sales_dashboards");

    Object.assign(component_x.spinner, {
      sales: true,
      month_to_date_sales: true,
      yesterday_sales: true,
      month_ave_sales: true,
      previous_month: true,
      last_30_days_chart: true,
      top_products: true,
      top_stores: true,
    });

    const siteId = secureStorage.getItem("defaultSiteId");
    const site = await HTTPRequest.getRequest(this.endPoint + "/sites/" + siteId, null);
    if (site != undefined) {
      component_x.site_type = site.site_type;
    }

    const today = moment();
    const yesterday = moment().subtract(1, "day");
    const currentMonthStart = moment().startOf("month");
    const previousMonthStart = moment().subtract(1, "month").startOf("month");
    const previousMonthEnd = moment().subtract(1, "month").endOf("month");
    const priorMonthStart = moment(previousMonthStart)
      .subtract(1, "month")
      .startOf("month");
    const priorMonthEnd = moment(previousMonthStart).subtract(1, "month").endOf("month");
    const yearAgoMonthStart = moment(previousMonthStart)
      .subtract(1, "year")
      .startOf("month");
    const yearAgoMonthEnd = moment(previousMonthStart).subtract(1, "year").endOf("month");
    const past30DaysStart = moment().subtract(29, "days");

    const currentTodaySummary = await this.fetchRangeSummary(
      today.format("YYYY-MM-DD"),
      today.format("YYYY-MM-DD"),
      siteId
    );
    const yesterdaySummary = await this.fetchRangeSummary(
      yesterday.format("YYYY-MM-DD"),
      yesterday.format("YYYY-MM-DD"),
      siteId
    );
    const currentMonthSummary = await this.fetchRangeSummary(
      currentMonthStart.format("YYYY-MM-DD"),
      today.format("YYYY-MM-DD"),
      siteId
    );
    const previousMonthSummary = await this.fetchRangeSummary(
      previousMonthStart.format("YYYY-MM-DD"),
      previousMonthEnd.format("YYYY-MM-DD"),
      siteId
    );
    const previousMonthProfit = await this.fetchRangeProfitSummary(
      previousMonthStart.format("YYYY-MM-DD"),
      previousMonthEnd.format("YYYY-MM-DD"),
      siteId
    );
    const previousMonthBasket = await this.fetchAverageBasketSize(
      previousMonthStart.format("YYYY-MM-DD"),
      previousMonthEnd.format("YYYY-MM-DD"),
      siteId
    );
    const priorMonthSummary = await this.fetchRangeSummary(
      priorMonthStart.format("YYYY-MM-DD"),
      priorMonthEnd.format("YYYY-MM-DD"),
      siteId
    );
    const yearAgoMonthSummary = await this.fetchRangeSummary(
      yearAgoMonthStart.format("YYYY-MM-DD"),
      yearAgoMonthEnd.format("YYYY-MM-DD"),
      siteId
    );

    const salesToday = this.summaryTotal(currentTodaySummary);
    const salesYesterday = this.summaryTotal(yesterdaySummary);
    const salesMonthToDate = this.summaryTotal(currentMonthSummary);
    const averageCurrentDays = Math.max(today.date(), 1);
    const averageDailyCurrent = salesMonthToDate / averageCurrentDays;

    Object.assign(component_x.dashboard.currentMonth, {
      salesToday: this.convertToCurrency(salesToday),
      salesYesterday: this.convertToCurrency(salesYesterday),
      salesMonthToDate: this.convertToCurrency(salesMonthToDate),
      averageDailySales: this.convertToCurrency(averageDailyCurrent),
    });

    component_x.spinner.sales = false;
    component_x.spinner.yesterday_sales = false;
    component_x.spinner.month_to_date_sales = false;
    component_x.spinner.month_ave_sales = false;

    const previousSales = this.summaryTotal(previousMonthSummary);
    const previousGrossMargin = this.grossMarginTotal(previousMonthProfit);
    const previousCost = previousSales - previousGrossMargin;
    const previousMonthDays = Math.max(previousMonthEnd.date(), 1);
    const previousAverageDailySales = previousSales / previousMonthDays;
    const grossMarginPercent =
      previousSales !== 0 ? (previousGrossMargin / previousSales) * 100 : 0;
    const yearAgoSales = this.summaryTotal(yearAgoMonthSummary);
    const priorMonthSales = this.summaryTotal(priorMonthSummary);
    const growthVsYearAgo = this.computeGrowthPercent(previousSales, yearAgoSales);
    const growthVsMonthAgo = this.computeGrowthPercent(previousSales, priorMonthSales);

    Object.assign(component_x.dashboard.previousMonth, {
      totalSales: this.convertToCurrency(previousSales),
      totalCost: this.convertToCurrency(previousCost),
      grossMargin: this.convertToCurrency(previousGrossMargin),
      grossMarginPercent: this.formatPercentage(grossMarginPercent),
      grossMarginPercentRaw: grossMarginPercent,
      growthVsYearAgo: this.formatPercentage(growthVsYearAgo),
      growthVsYearAgoRaw: growthVsYearAgo,
      growthVsMonthAgo: this.formatPercentage(growthVsMonthAgo),
      growthVsMonthAgoRaw: growthVsMonthAgo,
      averageDailySales: this.convertToCurrency(previousAverageDailySales),
      averageBasketSize: this.convertToCurrency(
        Number((previousMonthBasket && previousMonthBasket.average_basket_size) || 0)
      ),
    });

    component_x.spinner.previous_month = false;

    const last30DaysSeries = await this.fetchLast30DaysSales(siteId);
    this.renderLast30DaysChart(last30DaysSeries);
    component_x.spinner.last_30_days_chart = false;

    const topProductsData = await this.fetchTopProducts(
      past30DaysStart.format("YYYY-MM-DD"),
      today.format("YYYY-MM-DD"),
      siteId
    );
    component_x.dashboard.topProducts = topProductsData.map(function (item, index) {
      const totalQuantity = Number(item.quantity || 0);
      const totalSales = Number(item.amount || 0);
      const inventory = Number(item.onhand_quantity || 0);
      const dailyQtySold = totalQuantity / 30;
      const daysOnHand = dailyQtySold > 0 ? inventory / dailyQtySold : 0;

      return {
        rank: index + 1,
        productName: item.product_name || item.name || item.barcode || "Unnamed Product",
        dailySalesAvg: component_x.convertToCurrency(totalSales / 30),
        dailyQtySold: component_x.formatDecimal(dailyQtySold),
        inventory: component_x.formatInventory(inventory),
        inventoryRaw: inventory,
        daysOnHand: component_x.formatDecimal(daysOnHand),
      };
    });
    component_x.spinner.top_products = false;

    const topStoresBase = await this.fetchTopStores(
      past30DaysStart.format("YYYY-MM-DD"),
      today.format("YYYY-MM-DD")
    );
    const topStoresWithMargin = await Promise.all(
      topStoresBase.map(async function (store, index) {
        const refSiteId = store.id || store.site_id || store.siteId;
        let profitSummary = undefined;
        if (refSiteId != undefined && refSiteId !== null) {
          profitSummary = await component_x.fetchRangeProfitSummary(
            past30DaysStart.format("YYYY-MM-DD"),
            today.format("YYYY-MM-DD"),
            refSiteId
          );
        }

        const totalSales = Number(store.amount || 0);
        const totalGrossMarginRaw = component_x.grossMarginTotal(profitSummary);
        const totalCostRaw = totalSales - totalGrossMarginRaw;
        const grossMarginPercentRaw =
          totalSales !== 0 ? (totalGrossMarginRaw / totalSales) * 100 : 0;

        return {
          rank: index + 1,
          storeName: store.name || store.refcode || "Unnamed Store",
          totalSales: component_x.convertToCurrency(totalSales),
          totalCost: component_x.convertToCurrency(totalCostRaw),
          totalGrossMargin: component_x.convertToCurrency(totalGrossMarginRaw),
          totalGrossMarginRaw: totalGrossMarginRaw,
          grossMarginPercent: component_x.formatPercentage(grossMarginPercentRaw),
          grossMarginPercentRaw: grossMarginPercentRaw,
        };
      })
    );
    component_x.dashboard.topStores = topStoresWithMargin;
    component_x.spinner.top_stores = false;
  },
  beforeDestroy() {
    if (sales30DaysChart) {
      sales30DaysChart.destroy();
      sales30DaysChart = null;
    }
  },
  methods: {
    ...mapActions(["updateCurrentProfile"]),
    moment,
    buildScopedUrl(path, dateFrom, dateTo, siteId) {
      const params = ["date_from=" + dateFrom, "date_to=" + dateTo];
      if (this.site_type == "branch") {
        params.unshift("site_id=" + siteId);
      }
      return this.endPoint + path + "?" + params.join("&");
    },
    async fetchRangeSummary(dateFrom, dateTo, siteId) {
      return await HTTPRequest.getRequest(
        this.buildScopedUrl("pos_documents/range_summary", dateFrom, dateTo, siteId),
        null
      );
    },
    async fetchRangeProfitSummary(dateFrom, dateTo, siteId) {
      return await HTTPRequest.getRequest(
        this.buildScopedUrl(
          "pos_documents/range_profit_summary",
          dateFrom,
          dateTo,
          siteId
        ),
        null
      );
    },
    async fetchAverageBasketSize(dateFrom, dateTo, siteId) {
      return await HTTPRequest.getRequest(
        this.buildScopedUrl(
          "pos_documents/get_average_basket_size",
          dateFrom,
          dateTo,
          siteId
        ),
        null
      );
    },
    async fetchTopProducts(dateFrom, dateTo, siteId) {
      const params = ["top=10", "date_from=" + dateFrom, "date_to=" + dateTo];
      if (this.site_type == "branch") {
        params.splice(1, 0, "site_id=" + siteId);
      }
      return (
        (await HTTPRequest.getRequest(
          this.endPoint + "pos_documents/range_top_item_sales?" + params.join("&"),
          null
        )) || []
      );
    },
    async fetchTopStores(dateFrom, dateTo) {
      return (
        (await HTTPRequest.getRequest(
          this.endPoint +
            "pos_documents/range_top_site_sales?top=10&date_from=" +
            dateFrom +
            "&date_to=" +
            dateTo,
          null
        )) || []
      );
    },
    summaryTotal(summary) {
      if (summary == undefined) {
        return 0;
      }
      return Number(summary.total_sales || 0) - Number(summary.total_returns || 0);
    },
    grossMarginTotal(summary) {
      if (summary == undefined) {
        return 0;
      }
      return Number(summary.total_sales || 0);
    },
    computeGrowthPercent(currentValue, previousValue) {
      if (!previousValue) {
        return 0;
      }
      return ((currentValue - previousValue) / previousValue) * 100;
    },
    async fetchLast30DaysSales(siteId) {
      const days = Array.from({ length: 30 }, function (_, index) {
        return moment().subtract(29 - index, "days");
      });

      const responses = await Promise.all(
        days.map(async (day) => {
          const formattedDate = day.format("YYYY-MM-DD");
          const summary = await this.fetchRangeSummary(
            formattedDate,
            formattedDate,
            siteId
          );
          return {
            label: day.format("MMM D"),
            value: this.summaryTotal(summary),
          };
        })
      );

      return responses;
    },
    renderLast30DaysChart(series) {
      const canvas = document.getElementById("sales30DaysChart");
      if (!canvas) {
        return;
      }

      if (sales30DaysChart) {
        sales30DaysChart.destroy();
      }

      sales30DaysChart = new Chart(canvas, {
        type: "bar",
        data: {
          labels: series.map(function (item) {
            return item.label;
          }),
          datasets: [
            {
              label: "Sales",
              data: series.map(function (item) {
                return item.value;
              }),
              backgroundColor: "#55d5bf",
              borderColor: "#55d5bf",
              borderWidth: 1,
              borderRadius: 3,
              barPercentage: 0.76,
              categoryPercentage: 0.9,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          legend: {
            display: false,
          },
          tooltips: {
            callbacks: {
              label: function (tooltipItem) {
                return component_x.convertToCurrency(Number(tooltipItem.yLabel));
              },
            },
          },
          scales: {
            xAxes: [
              {
                gridLines: {
                  color: "rgba(219, 229, 240, 0.8)",
                  drawBorder: false,
                },
                ticks: {
                  fontColor: "#697a93",
                  maxRotation: 40,
                  minRotation: 40,
                },
              },
            ],
            yAxes: [
              {
                gridLines: {
                  color: "rgba(219, 229, 240, 0.8)",
                  drawBorder: false,
                },
                ticks: {
                  beginAtZero: true,
                  fontColor: "#697a93",
                  callback: function (value) {
                    return component_x.convertToCurrency(value);
                  },
                },
              },
            ],
          },
        },
      });
    },
    convertToCurrency(num) {
      const value = Number(num || 0);
      const absolute = Math.abs(value)
        .toFixed(2)
        .replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1,");
      return (value < 0 ? "-\u20B1" : "\u20B1") + absolute;
    },
    formatDecimal(num) {
      return Number(num)
        .toFixed(1)
        .replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1,");
    },
    formatInventory(num) {
      const value = Number(num || 0);
      if (Number.isInteger(value)) {
        return value.toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1,");
      }
      return value.toFixed(1).replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1,");
    },
    formatPercentage(num) {
      return Number(num).toFixed(1) + "%";
    },
    percentClass(value) {
      if (Number(value) > 0) {
        return "metric-figure-positive";
      }
      if (Number(value) < 0) {
        return "metric-figure-negative";
      }
      return "";
    },
    numberClass(value) {
      if (Number(value) > 0) {
        return "number-positive";
      }
      if (Number(value) <= 0) {
        return "number-negative";
      }
      return "";
    },
    setCurrentProfile(profile) {
      this.updateCurrentProfile(profile);
    },
  },
};
</script>

<style lang="scss">
.sales-dashboard-page {
  min-height: 100%;
  padding: 96px 24px 32px;
  background: linear-gradient(180deg, #f4f7fb 0%, #eef2f7 100%);
}

.dashboard-section {
  padding: 22px 22px 24px;
  margin-bottom: 22px;
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid #edf2f7;
  border-radius: 12px;
  box-shadow: 0 16px 34px rgba(29, 53, 87, 0.06);
}

.dashboard-section-header {
  margin-bottom: 14px;
}

.dashboard-section-header h2 {
  margin: 0;
  color: #173564;
  font-size: 1.2rem;
  font-weight: 800;
}

.metric-grid {
  display: grid;
  gap: 16px;
}

.metric-grid-current {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.metric-grid-history {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.metric-tile {
  min-height: 102px;
  padding: 14px 16px 12px;
  background: #fff;
  border: 1px solid #dbe5f0;
  border-radius: 10px;
}

.metric-kicker {
  margin: 0 0 6px;
  color: #7c8eaa;
  font-size: 0.85rem;
  font-weight: 800;
  text-transform: uppercase;
}

.metric-figure {
  margin: 0;
  color: #1f3d6c;
  font-size: clamp(1.8rem, 2vw, 2.3rem);
  font-weight: 800;
  line-height: 1.1;
}

.metric-note {
  display: inline-block;
  margin-top: 8px;
  color: #8aa0c2;
  font-size: 0.95rem;
  line-height: 1.2;
}

.metric-figure-positive {
  color: #28a745;
}

.metric-figure-negative {
  color: #c05a6a;
}

.dashboard-section-chart {
  padding-bottom: 16px;
}

.chart-panel {
  position: relative;
  height: 290px;
}

.dashboard-table {
  width: 100%;
  margin: 0;
  border-collapse: separate;
  border-spacing: 0;
  overflow: hidden;
  border: 1px solid #dbe5f0;
  border-radius: 10px;
  background: #fff;
}

.dashboard-table thead th {
  padding: 14px 12px;
  background: #f7f9fc;
  color: #7286a5;
  font-size: 0.74rem;
  font-weight: 800;
  text-align: left;
  text-transform: uppercase;
  border-bottom: 1px solid #e6edf5;
}

.dashboard-table tbody td {
  padding: 12px;
  color: #1f3d6c;
  font-size: 0.98rem;
  border-bottom: 1px solid #edf2f7;
}

.dashboard-table tbody tr:last-child td {
  border-bottom: 0;
}

.dashboard-table-name {
  font-weight: 700;
}

.dashboard-table-empty {
  text-align: center;
  color: #8aa0c2;
}

.rank-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  height: 28px;
  padding: 0 8px;
  border-radius: 999px;
  background: #eaf8f4;
  color: #20b796;
  font-size: 0.95rem;
  font-weight: 800;
}

.number-positive {
  color: #28a745;
}

.number-negative {
  color: #c05a6a;
}

.ant-spin-nested-loading,
.ant-spin-container {
  height: 100%;
}

@media (max-width: 1199.98px) {
  .metric-grid-current,
  .metric-grid-history {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .chart-panel {
    height: 260px;
  }
}

@media (max-width: 767.98px) {
  .sales-dashboard-page {
    padding: 72px 14px 24px;
  }

  .metric-grid-current,
  .metric-grid-history {
    grid-template-columns: 1fr;
  }

  .metric-figure {
    font-size: 1.85rem;
  }

  .chart-panel {
    height: 240px;
  }
}
</style>

